import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { createAuction } from "@/lib/api";
import type { Auction } from "@/lib/types";

const categories = [
	"Web Development",
	"Design",
	"DevOps",
	"Backend",
	"Content",
	"Mobile",
];

interface CreateAuctionDialogProps {
	onCreated: (auction: Auction) => void;
}

export function CreateAuctionDialog({ onCreated }: CreateAuctionDialogProps) {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState("");
	const [budget, setBudget] = useState("");
	const [deadlineHours, setDeadlineHours] = useState("48");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!title || !description || !category || !budget) return;
		setLoading(true);
		try {
			const auction = await createAuction({
				title,
				description,
				category,
				budget: Number(budget),
				deadlineHours: Number(deadlineHours),
				buyerId: "u1",
			});
			onCreated(auction);
			setOpen(false);
			setTitle("");
			setDescription("");
			setCategory("");
			setBudget("");
			setDeadlineHours("48");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button className="gap-2">
					<Plus className="h-4 w-4" />
					Post Request
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Post a Service Request</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4 mt-2">
					<div className="space-y-2">
						<Label htmlFor="title">Title</Label>
						<Input
							id="title"
							placeholder="e.g., Full-Stack E-Commerce Website"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="description">Description</Label>
						<Textarea
							id="description"
							placeholder="Describe what you need in detail..."
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							className="min-h-[100px] resize-none"
							required
						/>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label>Category</Label>
							<Select value={category} onValueChange={setCategory} required>
								<SelectTrigger>
									<SelectValue placeholder="Select" />
								</SelectTrigger>
								<SelectContent>
									{categories.map((c) => (
										<SelectItem key={c} value={c}>
											{c}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="budget">Budget ($)</Label>
							<Input
								id="budget"
								type="number"
								min="1"
								placeholder="5000"
								value={budget}
								onChange={(e) => setBudget(e.target.value)}
								required
							/>
						</div>
					</div>
					<div className="space-y-2">
						<Label htmlFor="deadline">Deadline (hours from now)</Label>
						<Select value={deadlineHours} onValueChange={setDeadlineHours}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="6">6 hours</SelectItem>
								<SelectItem value="12">12 hours</SelectItem>
								<SelectItem value="24">24 hours</SelectItem>
								<SelectItem value="48">48 hours</SelectItem>
								<SelectItem value="72">72 hours</SelectItem>
								<SelectItem value="168">1 week</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<Button type="submit" className="w-full" disabled={loading}>
						{loading ? "Creating..." : "Post Service Request"}
					</Button>
				</form>
			</DialogContent>
		</Dialog>
	);
}
